import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import {
  cancelPurchaseOrder,
  createPurchaseOrder,
  getPurchaseOrder,
  patchPurchaseOrderDraft,
  postGoodsReceipt,
  postSupplierPayment,
  submitPurchaseOrder,
  voidPurchaseOrder,
} from "@/shared/api/purchasing";
import { TagsAssigneesPanel } from "@/modules/tags/components/TagsAssigneesPanel";
import { DeskDocTabs } from "@/modules/shell/DeskDocTabs";
import { DeskDocActionRail } from "@/modules/shell/DeskDocActionRail";
import {
  DeskDocCreateMenu,
  type DeskDocCreateMenuItem,
} from "@/modules/shell/DeskDocCreateMenu";
import {
  pickPoPrimaryVerb,
  poCreateKinds,
} from "@/modules/shell/desk-doc-create-math";
import { DocActivity } from "@/modules/shell/DocActivity";
import { EmptyState } from "@/modules/shell/EmptyState";
import { DataTable } from "@/modules/shell/data-table";
import { DocNumberField } from "@/modules/core/components/DocNumberField";
import { PayMethodSelect } from "@/modules/core/components/PayMethodSelect";
import { WarehouseSelect } from "@/modules/inventory/components/WarehouseSelect";
import { DeskDocBackLink } from "@/modules/shell/DeskDocBackLink";
import { DocPartyStrip } from "@/modules/docs/DocPartyStrip";
import { AdvancedSection } from "@/modules/shell/AdvancedSection";
import { FieldGroup } from "@/modules/shell/FieldGroup";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  asCalendarYmd,
  formatDocDateLabel,
} from "@/modules/shell/relative-time";
import { useDeskDocTab } from "@/modules/shell/use-desk-doc-tab";
import { useRecordRecentDoc } from "@/modules/shell/use-record-recent-doc";
import { DocSlip, printDocSlip } from "@/modules/docs/print/DocSlip";
import { buildPoPrint } from "@/modules/docs/print/doc-print-math";
import { useOrgLetterhead } from "@/modules/docs/print/use-org-letterhead";
import { apiErrorCode, isApiError } from "@/shared/api/api-error";
import { newClientIdempotencyKey } from "@/shared/client-idempotency";
import { notify } from "@/shared/feedback/notify";
import { moneyDisplay, primaryDueBadge } from "@icraftx/shared/v1";
import { AxisProgress } from "@/modules/core/components/AxisProgress";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import {
  poLineColumns,
  poPaymentColumns,
  poReceiptColumns,
} from "./po-detail-columns";

const PO_DOC_TABS = ["order", "receipts", "payments"] as const;

export function PurchaseOrderDetailPage() {
  const { companySlug, orderId } = useParams();
  const { letterhead } = useOrgLetterhead(companySlug);
  const navigate = useNavigate();
  const confirmUi = useConfirm();
  const [data, setData] = useState<Awaited<
    ReturnType<typeof getPurchaseOrder>
  > | null>(null);
  const [recvQty, setRecvQty] = useState<Record<string, string>>({});
  const [recvLot, setRecvLot] = useState<Record<string, string>>({});
  const [recvSerial, setRecvSerial] = useState<Record<string, string>>({});
  const [payAmount, setPayAmount] = useState("");
  const [payMethodId, setPayMethodId] = useState<string | null>(null);
  const [bankRef, setBankRef] = useState("");
  const [supplierInvoiceRef, setSupplierInvoiceRef] = useState("");
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [receiveShipmentId, setReceiveShipmentId] = useState("");
  const [grNumber, setGrNumber] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draftRef, setDraftRef] = useState("");
  const [draftNotes, setDraftNotes] = useState("");
  const [draftPayDue, setDraftPayDue] = useState("");
  const [draftExpected, setDraftExpected] = useState("");
  const [draftDocDate, setDraftDocDate] = useState("");

  async function reload() {
    if (!companySlug || !orderId) return;
    const d = await getPurchaseOrder(companySlug, orderId);
    setData(d);
    const open = d.openShipments ?? [];
    setReceiveShipmentId((prev) => {
      if (prev && open.some((s) => s.id === prev)) return prev;
      return open.length === 1 ? open[0]!.id : "";
    });
    const init: Record<string, string> = {};
    for (const line of d.lines) {
      init[line.id] = line.qtyOpen;
    }
    setRecvQty(init);
    const outstanding = (
      Number(d.order.total) - Number(d.order.amountPaid)
    ).toFixed(2);
    setPayAmount(outstanding);
    setDraftRef(d.order.supplierRef ?? "");
    setDraftNotes(d.order.notes ?? "");
    setDraftPayDue(d.order.paymentDueOn ?? "");
    setDraftExpected(d.order.expectedOn ?? "");
    setDraftDocDate(asCalendarYmd(d.order.docDate));
  }

  useEffect(() => {
    let cancelled = false;
    reload().catch((err: unknown) => {
      if (cancelled) return;
      const msg = err instanceof Error ? err.message : "Failed";
      if (msg === "Not signed in") navigate("/login", { replace: true });
      else setError(msg);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per ids
  }, [companySlug, orderId, navigate]);

  const [docTab, setDocTab] = useDeskDocTab("order", PO_DOC_TABS);
  useRecordRecentDoc({
    companySlug,
    kind: "purchaseOrder",
    id: data?.order.id,
    label: data?.order.number,
    path:
      companySlug && data?.order.id
        ? `/c/${companySlug}/purchasing/orders/${data.order.id}`
        : undefined,
    hint: data?.order.supplierName,
  });

  if (!companySlug || !orderId) return null;
  if (!data) {
    return (
      <PageFrame width="narrow" className="py-4">
        {error ? (
          <Alert variant="destructive">{error}</Alert>
        ) : (
          <Alert variant="muted">Loading purchase order…</Alert>
        )}
      </PageFrame>
    );
  }

  const { order, lines, receipts, payments } = data;
  const gross = order.grossTotal ?? order.total;
  const printData = buildPoPrint({
    number: order.number,
    supplierName: order.supplierName,
    lifecycle: order.lifecycle,
    pay: order.pay,
    receive: order.receive,
    netTotal: order.netTotal,
    taxTotal: order.taxTotal,
    grossTotal: gross,
    amountPaid: order.amountPaid,
    supplierRef: order.supplierRef,
    primaryPhone: order.supplierPrimaryPhone,
    primaryEmail: order.supplierPrimaryEmail,
    primaryAddress: order.supplierPrimaryAddress,
    lines: lines.map((l) => ({
      productName: l.productName,
      qtyOrdered: l.qtyOrdered,
      unitCost: l.unitCost,
      lineGross: l.lineGross ?? l.lineTotal,
    })),
  });

  async function onReceive() {
    setBusy(true);
    setError(null);
    try {
      const payload = lines
        .map((l) => ({
          purchaseOrderLineId: l.id,
          qty: recvQty[l.id] ?? "0",
          ...(l.trackLot && recvLot[l.id]?.trim()
            ? { lotCode: recvLot[l.id]!.trim() }
            : {}),
          ...(l.trackSerial && recvSerial[l.id]?.trim()
            ? { serialCode: recvSerial[l.id]!.trim() }
            : {}),
        }))
        .filter((l) => Number(l.qty) > 0);
      if (payload.length === 0) throw new Error("Enter qty to receive");
      /** One key for soft-confirm retries so a flaky retry cannot double-receive — plan/24 */
      const idempotencyKey = newClientIdempotencyKey("gr");

      type ReceiveOpts = {
        lines: typeof payload;
        warehouseId?: string;
        supplierInvoiceRef?: string;
        number?: string;
        purchaseShipmentId?: string;
        confirmOverReceive?: boolean;
        confirmBypassRequireInTransit?: boolean;
        idempotencyKey: string;
      };

      const attempt = async (opts: ReceiveOpts) => {
        try {
          return await postGoodsReceipt(companySlug!, orderId!, opts);
        } catch (err: unknown) {
          const code = apiErrorCode(err);
          if (
            code === "require_purchase_in_transit" &&
            isApiError(err) &&
            !opts.confirmBypassRequireInTransit
          ) {
            const goShip = await confirmUi({
              title: "In-transit required",
              description: `${err.message}\n\nCreate an inbound in-transit shipment, then receive.`,
              confirmLabel: "Create in-transit",
              cancelLabel: "Not now",
            });
            if (goShip) {
              navigate(
                `/c/${companySlug}/purchasing/in-transit/new?purchaseOrderId=${order.id}`,
              );
              throw new Error("Redirecting to in-transit");
            }
            const bypass = await confirmUi({
              title: "Receive without in-transit?",
              description: "Requires override allow. This is audited.",
              confirmLabel: "Bypass & receive",
              destructive: true,
            });
            if (!bypass) throw new Error("Receive cancelled");
            return attempt({
              ...opts,
              confirmBypassRequireInTransit: true,
            });
          }
          if (
            code === "over_receive" &&
            isApiError(err) &&
            !opts.confirmOverReceive
          ) {
            const ok = await confirmUi({
              title: "Over-receive?",
              description: `${err.message}\n\nReceive anyway? Extra qty will increase on-hand.`,
              confirmLabel: "Receive anyway",
              destructive: true,
            });
            if (!ok) throw new Error("Receive cancelled");
            return attempt({ ...opts, confirmOverReceive: true });
          }
          throw err;
        }
      };

      const result = await attempt({
        lines: payload,
        ...(warehouseId ? { warehouseId } : {}),
        ...(supplierInvoiceRef.trim()
          ? { supplierInvoiceRef: supplierInvoiceRef.trim() }
          : {}),
        ...(grNumber.trim() ? { number: grNumber.trim() } : {}),
        ...(receiveShipmentId
          ? { purchaseShipmentId: receiveShipmentId }
          : {}),
        idempotencyKey,
      });
      navigate(`/c/${companySlug}/purchasing/receipts/${result.receipt.id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Receive failed");
    } finally {
      setBusy(false);
    }
  }

  async function onPay(direction: "to_supplier" | "from_supplier") {
    setBusy(true);
    setError(null);
    try {
      if (direction === "from_supplier") {
        const paid = Number(order.amountPaid);
        const amt = Number(payAmount);
        if (!(paid > 0)) {
          setError("Nothing paid yet to refund or credit against");
          return;
        }
        if (amt > paid) {
          const ok = await confirmUi({
            title: "Refund exceeds paid?",
            description: `Refund/credit ${payAmount} exceeds paid ${order.amountPaid}. Continue?`,
            confirmLabel: "Continue",
            destructive: true,
          });
          if (!ok) {
            setError("Refund cancelled");
            return;
          }
        }
      }
      const idempotencyKey = newClientIdempotencyKey("po-pay");
      const attempt = async (opts?: {
        confirmBypassRequireInvoice?: boolean;
      }) => {
        try {
          return await postSupplierPayment(companySlug!, {
            supplierId: order.supplierId,
            purchaseOrderId: order.id,
            amount: payAmount,
            direction,
            payMethodId: payMethodId ?? undefined,
            ...(bankRef.trim() ? { bankRef: bankRef.trim() } : {}),
            idempotencyKey,
            ...opts,
          });
        } catch (err: unknown) {
          if (
            isApiError(err) &&
            apiErrorCode(err) === "require_purchase_invoice" &&
            !opts?.confirmBypassRequireInvoice
          ) {
            const goInvoice = await confirmUi({
              title: "Invoice required",
              description: `${err.message}\n\nCreate a purchase invoice, then pay against it.`,
              confirmLabel: "Create invoice",
              cancelLabel: "Not now",
            });
            if (goInvoice) {
              navigate(
                `/c/${companySlug}/purchasing/invoices/new?purchaseOrderId=${order.id}`,
              );
              throw new Error("Redirecting to invoice");
            }
            const bypass = await confirmUi({
              title: "Pay without invoice?",
              description: "Requires override allow. This is audited.",
              confirmLabel: "Bypass & pay",
              destructive: true,
            });
            if (!bypass) throw new Error("Pay cancelled");
            return attempt({ confirmBypassRequireInvoice: true });
          }
          throw err;
        }
      };
      const result = await attempt();
      if (result.overpay) {
        notify.info(
          "Paid over balance — extra kept as unapplied supplier credit",
        );
      } else {
        notify.success("Payment recorded");
      }
      navigate(`/c/${companySlug}/purchasing/payments/${result.payment.id}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Payment failed";
      if (msg !== "Redirecting to invoice" && msg !== "Pay cancelled") {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  const railPrimary =
    order.lifecycle === "open"
      ? pickPoPrimaryVerb({
          pay: order.pay,
          receive: order.receive,
          paymentDueOn: order.paymentDueOn,
          expectedOn: order.expectedOn,
        })
      : null;
  const railCreateItems: DeskDocCreateMenuItem[] =
    order.lifecycle === "open"
      ? poCreateKinds({
          primary: railPrimary,
          pay: order.pay,
          receive: order.receive,
        }).map((kind) => {
          if (kind === "invoice") {
            return {
              id: "invoice",
              label: "Invoice",
              to: `/c/${companySlug}/purchasing/invoices/new?purchaseOrderId=${orderId}`,
            };
          }
          if (kind === "in-transit") {
            return {
              id: "in-transit",
              label: "In transit",
              to: `/c/${companySlug}/purchasing/in-transit/new?purchaseOrderId=${orderId}`,
            };
          }
          if (kind === "receipt") {
            return {
              id: "receipt",
              label: "Goods receipt",
              onSelect: () => void onReceive(),
            };
          }
          return {
            id: "payment",
            label: "Payment",
            disabled: !payAmount.trim(),
            title: !payAmount.trim()
              ? "Enter amount in Supplier payment below"
              : undefined,
            onSelect: () => void onPay("to_supplier"),
          };
        })
      : [];

  async function onLifecycle(action: "submit" | "cancel" | "void") {
    const number = data?.order.number;
    if (action === "cancel") {
      const ok = await confirmUi({
        title: `Cancel ${number ?? "draft"}?`,
        description: "The draft will be voided. This cannot be undone.",
        confirmLabel: "Cancel draft",
        destructive: true,
      });
      if (!ok) return;
    }
    if (action === "void") {
      const ok = await confirmUi({
        title: `Void ${number ?? "purchase order"}?`,
        description:
          "Voiding is permanent — you cannot reopen this order. Receipts and payments must already be clear.",
        confirmLabel: "Void order",
        destructive: true,
      });
      if (!ok) return;
    }
    setBusy(true);
    setError(null);
    try {
      if (action === "submit")
        await submitPurchaseOrder(companySlug!, orderId!);
      else if (action === "cancel")
        await cancelPurchaseOrder(companySlug!, orderId!);
      else await voidPurchaseOrder(companySlug!, orderId!);
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Lifecycle failed");
    } finally {
      setBusy(false);
    }
  }

  async function onDuplicate() {
    setBusy(true);
    setError(null);
    try {
      const taxMode =
        order.taxPriceMode === "inclusive" || order.taxPriceMode === "exclusive"
          ? order.taxPriceMode
          : undefined;
      const { order: draft } = await createPurchaseOrder(companySlug!, {
        supplierId: order.supplierId,
        lines: lines.map((l) => ({
          productId: l.productId,
          qty: l.qtyOrdered,
          unitCost: l.unitCost,
        })),
        submit: false,
        ...(order.taxRatePct?.trim() ? { taxRatePct: order.taxRatePct } : {}),
        ...(taxMode ? { taxPriceMode: taxMode } : {}),
        ...(order.supplierRef?.trim()
          ? { supplierRef: order.supplierRef.trim() }
          : {}),
        ...(order.paymentDueOn ? { paymentDueOn: order.paymentDueOn } : {}),
        ...(order.expectedOn ? { expectedOn: order.expectedOn } : {}),
      });
      notify.success(`Draft ${draft.number} created`);
      navigate(`/c/${companySlug}/purchasing/orders/${draft.id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Duplicate failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageFrame width="narrow">
      <div className="pos-receipt-print" aria-hidden>
        <DocSlip data={printData} letterhead={letterhead} />
      </div>
      <div className="no-print">
        <div className="mb-3">
          <DeskDocBackLink
            listBasePath={`/c/${companySlug}/purchasing/orders`}
            listLabel="Purchase orders"
          />
        </div>
        <PageHeader
          eyebrow="Purchase order"
          title={order.number}
          description={[
            order.docDate
              ? `Date ${formatDocDateLabel(order.docDate)}`
              : null,
            primaryDueBadge({
              paymentDueOn: order.paymentDueOn,
              expectedOn: order.expectedOn,
            }).label || null,
            order.supplierRef ? `ref ${order.supplierRef}` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
          actions={
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => void onDuplicate()}
              >
                Duplicate
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => printDocSlip()}
              >
                Print
              </Button>
            </div>
          }
        />
        <div className="mt-2">
          <LifecycleStatus value={order.lifecycle} showLabel />
        </div>
        <DocPartyStrip
          companySlug={companySlug}
          partyId={order.supplierId}
          partyName={order.supplierName}
          roleLabel="Supplier"
          primaryPhone={order.supplierPrimaryPhone}
          primaryEmail={order.supplierPrimaryEmail}
          primaryAddress={order.supplierPrimaryAddress}
          partyNotes={order.supplierNotes}
          docNotes={order.notes}
        />
        <div className="mt-3 flex flex-wrap gap-6">
          <AxisProgress
            className="min-w-[10rem] max-w-xs flex-1"
            label="Paid"
            axis="Paid"
            format="money"
            current={order.amountPaid}
            max={order.total || order.grossTotal}
          />
          <AxisProgress
            className="min-w-[10rem] max-w-xs flex-1"
            label="Received"
            axis="Received"
            format="qty"
            current={order.qtyReceived}
            max={order.qtyOrdered}
          />
        </div>
        {orderId ? (
          <TagsAssigneesPanel
            companySlug={companySlug}
            docType="purchase_order"
            docId={orderId}
            linkPath={`/c/${companySlug}/purchasing/orders/${orderId}`}
          />
        ) : null}
        {order.lifecycle === "open" ? (
          <DeskDocActionRail className="mt-4">
            {railPrimary === "receive" ? (
              <Button
                type="button"
                size="sm"
                disabled={busy}
                onClick={() => void onReceive()}
              >
                Post goods receipt
              </Button>
            ) : null}
            {railPrimary === "pay" ? (
              <Button
                type="button"
                size="sm"
                disabled={busy || !payAmount.trim()}
                onClick={() => void onPay("to_supplier")}
              >
                Pay supplier
              </Button>
            ) : null}
            <DeskDocCreateMenu items={railCreateItems} disabled={busy} />
          </DeskDocActionRail>
        ) : null}
        <DeskDocTabs
          aria-label="Purchase order views"
          value={docTab}
          onChange={setDocTab}
          items={[
            { id: "order", label: "Order" },
            {
              id: "receipts",
              label: "Receipts",
              count: receipts.length,
            },
            {
              id: "payments",
              label: "Payments",
              count: payments.length,
            },
          ]}
        />
      </div>

      {docTab === "order" &&
        (order.lifecycle === "draft" || order.lifecycle === "open") && (
        <form
          className="no-print mt-4 space-y-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)]/60 p-4"
          onSubmit={(e) => {
            e.preventDefault();
            void (async () => {
              setBusy(true);
              setError(null);
              setStatus(null);
              try {
                const save = async (confirmOverBackdate?: boolean) => {
                  try {
                    if (order.lifecycle === "draft") {
                      await patchPurchaseOrderDraft(companySlug, orderId, {
                        supplierRef: draftRef.trim() || null,
                        notes: draftNotes.trim() || null,
                        paymentDueOn: draftPayDue.trim() || null,
                        expectedOn: draftExpected.trim() || null,
                        ...(draftDocDate.trim()
                          ? { docDate: draftDocDate.trim() }
                          : {}),
                        ...(confirmOverBackdate
                          ? { confirmOverBackdate: true }
                          : {}),
                      });
                    } else {
                      await patchPurchaseOrderDraft(companySlug, orderId, {
                        docDate: draftDocDate.trim(),
                        ...(confirmOverBackdate
                          ? { confirmOverBackdate: true }
                          : {}),
                      });
                    }
                  } catch (err: unknown) {
                    if (
                      apiErrorCode(err) === "doc_date_backdate" &&
                      isApiError(err) &&
                      !confirmOverBackdate
                    ) {
                      const ok = await confirmUi({
                        title: "Backdated document date?",
                        description: err.message,
                        confirmLabel: "Save anyway",
                      });
                      if (!ok) throw new Error("Save cancelled");
                      return save(true);
                    }
                    throw err;
                  }
                };
                await save();
                setStatus(
                  order.lifecycle === "draft"
                    ? "Updated draft"
                    : "Updated date",
                );
                notify.updated(order.number);
                await reload();
              } catch (err: unknown) {
                const msg =
                  err instanceof Error ? err.message : "Save failed";
                setError(msg);
                notify.error(msg);
              } finally {
                setBusy(false);
              }
            })();
          }}
        >
          <h2 className="m-0 text-sm font-semibold">
            {order.lifecycle === "draft" ? "Edit draft" : "Edit date"}
          </h2>
          {order.lifecycle === "draft" ? (
            <>
              <label className="block text-xs text-[var(--color-muted)]">
                Supplier ref
                <Input
                  className="mt-1"
                  value={draftRef}
                  onChange={(e) => setDraftRef(e.target.value)}
                  disabled={busy}
                />
              </label>
              <label className="block text-xs text-[var(--color-muted)]">
                Notes
                <Input
                  className="mt-1"
                  value={draftNotes}
                  onChange={(e) => setDraftNotes(e.target.value)}
                  disabled={busy}
                />
              </label>
            </>
          ) : null}
          <div className="flex flex-wrap gap-3">
            <label className="block text-xs text-[var(--color-muted)]">
              Date
              <Input
                type="date"
                className="mt-1"
                value={draftDocDate}
                onChange={(e) => setDraftDocDate(e.target.value)}
                disabled={busy}
                required={order.lifecycle === "open"}
              />
            </label>
            {order.lifecycle === "draft" ? (
              <>
                <label className="block text-xs text-[var(--color-muted)]">
                  Payment due
                  <Input
                    type="date"
                    className="mt-1"
                    value={draftPayDue}
                    onChange={(e) => setDraftPayDue(e.target.value)}
                    disabled={busy}
                  />
                </label>
                <label className="block text-xs text-[var(--color-muted)]">
                  Expected
                  <Input
                    type="date"
                    className="mt-1"
                    value={draftExpected}
                    onChange={(e) => setDraftExpected(e.target.value)}
                    disabled={busy}
                  />
                </label>
              </>
            ) : null}
          </div>
          <Button type="submit" size="sm" pending={busy} pendingLabel="Saving…">
            {order.lifecycle === "draft" ? "Save draft" : "Save date"}
          </Button>
        </form>
      )}

      {docTab === "order" && (
        <>
      {status && (
        <Alert variant="muted" className="no-print mt-3">
          {status}
        </Alert>
      )}

      {(order.lifecycle === "draft" || order.lifecycle === "open") && (
        <div className="no-print mt-4 flex flex-wrap gap-2">
          {order.lifecycle === "draft" && (
            <>
              <Button
                type="button"
                disabled={busy}
                onClick={() => void onLifecycle("submit")}
              >
                Submit
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => void onLifecycle("cancel")}
              >
                Cancel draft
              </Button>
            </>
          )}
          {order.lifecycle === "open" &&
            order.receive === "none" &&
            order.pay === "unpaid" && (
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                onClick={() => void onLifecycle("void")}
              >
                Void order
              </Button>
            )}
        </div>
      )}
      <FieldGroup title="Totals" className="mt-3" level="sub">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 font-mono text-sm tabular-nums">
          <dt className="text-[var(--color-muted)]">Net</dt>
          <dd className="m-0">{moneyDisplay(order.netTotal ?? gross)}</dd>
          <dt className="text-[var(--color-muted)]">Tax</dt>
          <dd className="m-0">{moneyDisplay(order.taxTotal ?? "0.00")}</dd>
          <dt className="text-[var(--color-muted)]">Total</dt>
          <dd className="m-0 font-medium">{moneyDisplay(gross)}</dd>
          <dt className="text-[var(--color-muted)]">Paid</dt>
          <dd className="m-0">{moneyDisplay(order.amountPaid)}</dd>
        </dl>
      </FieldGroup>

      <AdvancedSection
        className="no-print"
        subtitle="tax · refs · dates"
      >
        <FieldGroup title="Tax" level="sub">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-[var(--color-muted)]">Rate</dt>
            <dd className="m-0 font-mono">
              {order.taxRatePct ?? "0"}% {order.taxPriceMode ?? ""}
            </dd>
          </dl>
        </FieldGroup>
        <FieldGroup title="Refs & dates" level="sub">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            {order.supplierRef ? (
              <>
                <dt className="text-[var(--color-muted)]">Supplier ref</dt>
                <dd className="m-0">{order.supplierRef}</dd>
              </>
            ) : null}
            {order.paymentDueOn ? (
              <>
                <dt className="text-[var(--color-muted)]">Payment due</dt>
                <dd className="m-0">{order.paymentDueOn}</dd>
              </>
            ) : null}
            {order.expectedOn ? (
              <>
                <dt className="text-[var(--color-muted)]">Expect by</dt>
                <dd className="m-0">{order.expectedOn}</dd>
              </>
            ) : null}
            {!order.supplierRef &&
            !order.paymentDueOn &&
            !order.expectedOn ? (
              <dd className="m-0 text-[var(--color-muted)]">None set</dd>
            ) : null}
          </dl>
        </FieldGroup>
      </AdvancedSection>

      <DataTable
        listId="po-detail-lines"
        companySlug={companySlug}
        className="mt-8"
        columns={poLineColumns({
          canReceive: order.lifecycle === "open",
          recvQty,
          setRecvQty,
          recvLot,
          setRecvLot,
          recvSerial,
          setRecvSerial,
        })}
        data={lines}
        getRowId={(r) => r.id}
        manualSorting={false}
        empty={
          <EmptyState
            title="No lines"
            description="This order has no product lines."
          />
        }
      />

      {order.lifecycle === "open" && (
        <div className="mt-6 flex flex-col gap-4">
          <WarehouseSelect
            companySlug={companySlug}
            value={warehouseId}
            onChange={setWarehouseId}
            disabled={busy}
            label="Receive into"
          />
          {(data.openShipments?.length ?? 0) > 0 ? (
            <label className="block text-xs text-[var(--color-muted)]">
              Receive against in-transit
              <Select
                className="mt-1 max-w-xs"
                value={receiveShipmentId}
                onChange={(e) => setReceiveShipmentId(e.target.value)}
                disabled={busy}
              >
                <option value="">None (direct receive)</option>
                {(data.openShipments ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.number}
                  </option>
                ))}
              </Select>
            </label>
          ) : null}
          <DocNumberField
            companySlug={companySlug}
            docType="goods_receipt"
            value={grNumber}
            onChange={setGrNumber}
          />
          <label className="text-sm">
            <span className="text-[var(--color-muted)]">
              Supplier invoice # (optional)
            </span>
            <Input className="mt-1 max-w-xs" value={supplierInvoiceRef} onChange={(e) => setSupplierInvoiceRef(e.target.value)}
              maxLength={120}
            />
          </label>
          <Button
            type="button"
            disabled={busy}
            onClick={() => void onReceive()}
          >
            Post goods receipt
          </Button>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-sm">
              <span className="text-[var(--color-muted)]">Method</span>
              <div className="mt-1 w-44">
                <PayMethodSelect
                  companySlug={companySlug}
                  value={payMethodId}
                  onChange={setPayMethodId}
                  disabled={busy}
                />
              </div>
            </label>
            <label className="text-sm">
              <span className="text-[var(--color-muted)]">Bank / ref</span>
              <Input className="mt-1 w-40" value={bankRef} onChange={(e) => setBankRef(e.target.value)}
                maxLength={120}
              />
            </label>
            <label className="text-sm">
              <span className="text-[var(--color-muted)]">Pay amount</span>
              <Input
                className="mt-1 w-40 font-mono text-right tabular-nums"
                inputMode="decimal"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
              />
            </label>
            {(() => {
              const outstanding =
                Number(order.total) - Number(order.amountPaid);
              const amt = Number(payAmount);
              const willOverpay =
                Number.isFinite(amt) &&
                amt > 0 &&
                Number.isFinite(outstanding) &&
                amt > outstanding + 1e-9;
              if (!willOverpay) return null;
              return (
                <Alert variant="destructive" className="basis-full py-2 text-xs">
                  Amount exceeds outstanding {moneyDisplay(String(outstanding))}{" "}
                  — extra becomes unapplied credit. Pay still goes through.
                </Alert>
              );
            })()}
            <Button
              type="button"
              variant="outline"
              disabled={busy || !payAmount.trim()}
              onClick={() => void onPay("to_supplier")}
            >
              Pay supplier
            </Button>
            {Number(order.amountPaid) > 0 && (
              <Button
                type="button"
                variant="ghost"
                disabled={busy || !payAmount.trim()}
                onClick={() => void onPay("from_supplier")}
              >
                Record refund / credit
              </Button>
            )}
          </div>
          {Number(order.amountPaid) > 0 && (
            <p className="m-0 text-xs text-[var(--color-muted)]">
              Refund / credit uses the same amount and method — plan/17
              payment from supplier.
            </p>
          )}
        </div>
      )}
        </>
      )}

      {docTab === "receipts" && (
        <DataTable
          listId="po-detail-receipts"
          companySlug={companySlug}
          columns={poReceiptColumns(companySlug)}
          data={receipts}
          getRowId={(r) => r.id}
          manualSorting={false}
          empty={
            <EmptyState
              title="No receipts yet"
              description="Post a goods receipt from the Order tab when stock arrives."
            />
          }
        />
      )}

      {docTab === "payments" && (
        <DataTable
          listId="po-detail-payments"
          companySlug={companySlug}
          columns={poPaymentColumns(companySlug)}
          data={payments}
          getRowId={(r) => r.id}
          manualSorting={false}
          empty={
            <EmptyState
              title="No payments yet"
              description="Pay the supplier from the Order tab when due."
            />
          }
        />
      )}

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}

      {orderId ? (
        <DocActivity
          companySlug={companySlug}
          docType="purchase_order"
          docId={orderId}
          linkPath={`/c/${companySlug}/purchasing/orders/${orderId}`}
        />
      ) : null}
    </PageFrame>
  );
}
